// Code-drawn How to Play illustrations (§14.2: "6 illustrated cards"): little toy-box dioramas in
// the §12.2 palette with chunky ink outlines, built from the same bean and icon drawings as the
// rest of the UI. Each returns an inline SVG (320 × 200).
import { beanMarkup, iconMarkup } from './icons';
import { SVG_NS } from './ui';

const INK = '#2A1A3A';
const W = 320;
const H = 200;

function bean(x: number, y: number, s: number, color: string, mood: 'happy' | 'out' | 'ghost' | 'dizzy' = 'happy', rot = 0, flip = false): string {
  return `<g transform="translate(${x} ${y}) rotate(${rot}) scale(${flip ? -s : s} ${s}) translate(-12 -12)">${beanMarkup(color, mood)}</g>`;
}

function iconAt(name: string, x: number, y: number, s: number, rot = 0): string {
  return `<g transform="translate(${x} ${y}) rotate(${rot}) scale(${s}) translate(-12 -12)">${iconMarkup(name)}</g>`;
}

function pop(text: string, x: number, y: number, size: number, fill: string, rot = -6): string {
  return `<text x="${x}" y="${y}" transform="rotate(${rot} ${x} ${y})" text-anchor="middle" font-family="'Lilita One', sans-serif" font-size="${size}" fill="${fill}" stroke="${INK}" stroke-width="${size * 0.14}" paint-order="stroke" stroke-linejoin="round">${text}</text>`;
}

function burst(x: number, y: number, r: number, fill: string): string {
  const pts: string[] = [];
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    const rr = i % 2 === 0 ? r : r * 0.58;
    pts.push(`${(x + Math.cos(a) * rr).toFixed(1)},${(y + Math.sin(a) * rr).toFixed(1)}`);
  }
  return `<polygon points="${pts.join(' ')}" fill="${fill}" stroke="${INK}" stroke-width="3" stroke-linejoin="round"/>`;
}

function backdrop(top: string, bottom: string, id: string): string {
  return `<defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${top}"/><stop offset="1" stop-color="${bottom}"/></linearGradient></defs>`
    + `<rect x="0" y="0" width="${W}" height="${H}" rx="18" fill="url(#${id})"/>`
    + `<circle cx="40" cy="36" r="3" fill="#fff" opacity=".7"/><circle cx="270" cy="28" r="2.4" fill="#fff" opacity=".7"/><circle cx="292" cy="62" r="1.8" fill="#fff" opacity=".6"/>`;
}

/** A floating cardboard slab from x0 to x1 with its top at y. */
function slab(x0: number, x1: number, y: number, top = '#D9A86C', side = '#B4834C'): string {
  return `<path d="M${x0} ${y}h${x1 - x0}v22a8 8 0 0 1-8 8H${x0 + 8}a8 8 0 0 1-8-8z" fill="${side}" stroke="${INK}" stroke-width="3.5" stroke-linejoin="round"/>`
    + `<path d="M${x0 + 4} ${y - 14}h${x1 - x0 - 8}a6 6 0 0 1 4 10l-0 0H${x0}a6 6 0 0 1 4-10z" fill="${top}" stroke="${INK}" stroke-width="3.5" stroke-linejoin="round"/>`
    + `<path d="M${x0 + 14} ${y - 8}h${(x1 - x0) * 0.4}" stroke="#FFF1C9" stroke-width="3" stroke-linecap="round" opacity=".7"/>`;
}

function shadow(x: number, y: number, r: number, o = 0.25): string {
  return `<ellipse cx="${x}" cy="${y}" rx="${r}" ry="${r * 0.3}" fill="${INK}" opacity="${o}"/>`;
}

function speedLines(x: number, y: number, len: number, dir: 1 | -1): string {
  return [0, 12, 24].map((dy, i) => `<path d="M${x} ${y + dy - 12}h${dir * (len - i * 8)}" stroke="${INK}" stroke-width="3" stroke-linecap="round" opacity=".55"/>`).join('');
}

function arrow(d: string, color = INK): string {
  return `<path d="${d}" fill="none" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-dasharray="2 9"/>`;
}

const SCENES: Readonly<Record<string, () => string>> = {
  shove: () => backdrop('#FFD6A5', '#BDB2FF', 'g1') + slab(24, 248, 150)
    + shadow(96, 142, 22) + shadow(188, 142, 22)
    + speedLines(54, 112, 34, -1)
    + bean(100, 110, 2.6, '#FF5A4E', 'happy', 12)
    + burst(150, 104, 26, '#FFE08A')
    + bean(196, 104, 2.6, '#4CB8FF', 'dizzy', -18, true)
    + pop('BONK!', 150, 72, 30, '#FF4F9A')
    + arrow('M214 86q40 -18 58 32') + iconAt('arrowR', 278, 128, 1.1, 70),
  grab: () => backdrop('#FFE3C8', '#C8B6FF', 'g2') + slab(24, 210, 150)
    + shadow(110, 142, 24)
    + bean(110, 112, 2.6, '#3DDC97', 'happy')
    + bean(116, 64, 2.2, '#FF7AC8', 'dizzy', 90)
    + `<path d="M92 96q-8-18 6-30M128 96q10-16-2-30" stroke="${INK}" stroke-width="3.5" fill="none" stroke-linecap="round"/>`
    + pop('YOINK!', 54, 50, 20, '#FFC93C', -10)
    + arrow('M140 56q70 -40 120 40') + iconAt('arrowR', 262, 104, 1.1, 60)
    + pop('YEET!', 236, 46, 28, '#4CB8FF', 8),
  edges: () => backdrop('#FFD6A5', '#BDB2FF', 'g3') + slab(20, 170, 140)
    + bean(70, 100, 2.6, '#FF9A3C', 'dizzy', 22)
    + `<path d="M50 86l-16-10M92 84l16-12" stroke="${INK}" stroke-width="3.5" stroke-linecap="round"/>`
    + pop('!', 70, 50, 34, '#FF4D4D', 0)
    + `<g><circle cx="150" cy="127" r="7" fill="#FFF1C9" stroke="${INK}" stroke-width="3"/><circle cx="166" cy="127" r="7" fill="#FFF1C9" stroke="${INK}" stroke-width="3"/>`
    + `<circle cx="158" cy="127" r="18" fill="none" stroke="#FF4D4D" stroke-width="2.5" stroke-dasharray="4 4"/></g>`
    + bean(186, 162, 2, '#9B6BFF', 'dizzy', -8)
    + bean(222, 86, 2.4, '#4CB8FF', 'happy', -14)
    + iconAt('arrowR', 196, 110, 1.2, 130)
    + pop('CRUNCH!', 248, 58, 22, '#FFC93C', 6),
  dizzy: () => {
    const rows = [[0, 18, '#3DDC97'], [50, 48, '#FFC93C'], [100, 92, '#FF4D4D']] as const;
    return backdrop('#FFE1EC', '#B9E6FF', 'g4')
      + `<path d="M28 126a62 62 0 0 1 124 0z" fill="#FFF6E9" stroke="${INK}" stroke-width="4"/>`
      + `<path d="M40 126a50 50 0 0 1 20-40" stroke="#3DDC97" stroke-width="12" fill="none"/>`
      + `<path d="M60 86a50 50 0 0 1 46-10" stroke="#FFC93C" stroke-width="12" fill="none"/>`
      + `<path d="M106 76a50 50 0 0 1 34 50" stroke="#FF4D4D" stroke-width="12" fill="none"/>`
      + `<path d="M90 126L124 88" stroke="${INK}" stroke-width="5" stroke-linecap="round"/><circle cx="90" cy="126" r="9" fill="${INK}"/>`
      + iconAt('star', 64, 60, 1.1, -10) + iconAt('star', 116, 52, 0.9, 12)
      + rows.map(([pct, len, color], i) => {
        const y = 60 + i * 42;
        return `<text x="176" y="${y}" font-family="'Lilita One', sans-serif" font-size="15" fill="${INK}">${pct}%</text>`
          + `<rect x="214" y="${y - 14}" width="${len}" height="16" rx="8" fill="${color}" stroke="${INK}" stroke-width="3"/>`
          + `<text x="${220 + len}" y="${y}" font-family="'Lilita One', sans-serif" font-size="13" fill="${INK}" text-anchor="start">${i === 0 ? '3m' : i === 1 ? '8m' : '15m'}</text>`;
      }).join('')
      + pop('FLY!', 260, 180, 22, '#FF4F9A', -4);
  },
  items: () => {
    const items = ['banana', 'bomb', 'glove', 'balloon', 'plunger', 'shake'];
    return backdrop('#FFD6A5', '#BDB2FF', 'g5')
      + `<rect x="112" y="66" width="96" height="72" rx="10" fill="#E8B87A" stroke="${INK}" stroke-width="4"/>`
      + `<path d="M112 92h96M160 66v26" stroke="${INK}" stroke-width="3"/>`
      + `<circle cx="146" cy="112" r="9" fill="#fff" stroke="${INK}" stroke-width="3"/><circle cx="174" cy="112" r="9" fill="#fff" stroke="${INK}" stroke-width="3"/>`
      + `<circle cx="148" cy="114" r="4" fill="${INK}"/><circle cx="172" cy="110" r="4" fill="${INK}"/>`
      + pop('?', 160, 56, 34, '#FFC93C', 8)
      + items.map((n, i) => {
        const a = (i / items.length) * Math.PI * 2 - Math.PI / 2;
        return iconAt(n, 160 + Math.cos(a) * 118, 102 + Math.sin(a) * 74, 1.9, (i % 2 ? 1 : -1) * 8);
      }).join('');
  },
  ghosts: () => backdrop('#C8B6FF', '#8FB8FF', 'g6') + slab(40, 280, 160)
    + `<g opacity=".85">${bean(110, 52, 2.4, '#E8F4FF', 'ghost', -6)}</g>`
    + `<path d="M110 76v30" stroke="${INK}" stroke-width="3" stroke-dasharray="3 6" stroke-linecap="round"/>`
    + iconAt('pebble', 110, 112, 1.3)
    + `<ellipse cx="110" cy="146" rx="20" ry="6" fill="none" stroke="#FF4F9A" stroke-width="3" stroke-dasharray="5 4"/>`
    + bean(160, 124, 2.4, '#FFC93C', 'dizzy', 16)
    + pop('PLINK!', 196, 92, 22, '#FFFFFF', -8)
    + `<g opacity=".8">${bean(244, 44, 1.6, '#E8F4FF', 'ghost', 10)}</g>` + iconAt('honk', 274, 34, 1, 14),
};

export const HOW_TO_SCENES = ['shove', 'grab', 'edges', 'dizzy', 'items', 'ghosts'] as const;
export type HowToScene = (typeof HOW_TO_SCENES)[number];

/** The illustration for one How to Play card. */
export function illustration(scene: HowToScene): SVGSVGElement {
  const svgEl = document.createElementNS(SVG_NS, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'illus');
  svgEl.setAttribute('aria-hidden', 'true');
  svgEl.innerHTML = SCENES[scene]();
  return svgEl;
}
